/* eslint-disable camelcase */
import { useMutation } from '@tanstack/react-query'
import { ReactQueryKeys } from '@/types'
import { useContext } from 'react'
import { AxiosContext } from '@/context/Axios'
import { resizeFile } from '@/utils/helper'
import { useLocalStorage } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import { useUploadMedia } from './useUploadMedia'

interface param {
  file: File
  fileType?: number
}

export const useUploadCoverPicture = () => {
  const axios = useContext(AxiosContext)
  const uploadFile = useUploadMedia()
  const [basicInfo] = useLocalStorage('ewBasicInfo', null)
  // See useUploadProfilePicture — the reactive store catches up even when this
  // component-scoped localStorage cache was read before basic info had loaded.
  const storeContainerID = useAppStore((state) => state.user.profile?.mobileContID)
  const containerID = storeContainerID || basicInfo?.mobileContID || ''
  return useMutation({
    mutationKey: [ReactQueryKeys.UPLOAD_COVER_PICTURE, containerID],
    mutationFn: async ({ file }: param): Promise<any> => {
      if (!containerID) {
        throw new Error('User must be authenticated to upload a cover picture')
      }
      const image = await resizeFile(file)

      // Upload image
      const photo = await uploadFile.mutateAsync(image as string)

      // Save set upload image as cover
      const response = await axios.api.post('ajax/users', {
        requestType: 'profileCoverSave',
        format: 'json',
        profileCoverID: photo.egoID,
        profileCoverHash: photo.egoHash,
      })
      return response
    },
  })
}

/** Clears the cover photo — same `profileCoverSave` endpoint, with empty IDs to unset it. */
export const useRemoveCoverPicture = () => {
  const axios = useContext(AxiosContext)
  return useMutation({
    mutationKey: [ReactQueryKeys.REMOVE_COVER_PICTURE],
    mutationFn: async (): Promise<any> => {
      const response = await axios.api.post('ajax/users', {
        requestType: 'profileCoverSave',
        format: 'json',
        profileCoverID: '',
        profileCoverHash: '',
      })
      return response
    },
  })
}
